import { inject, Injectable } from '@angular/core';
import { LabelLayout, LabelLayoutConfig } from '../models/label-layout.model';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Service for reading label layout definitions from local SQLite.
 * Label layouts define how QR code and text are arranged within a single label.
 * Read-only — layouts are seeded as system data.
 */
@Injectable({
  providedIn: 'root',
})
export class LabelLayoutService {
  private db = inject(DatabaseService);
  private logger = inject(LoggerService).getLogger('label-layouts');

  // ---------------------------------------------------------------------------
  // Parsing
  // ---------------------------------------------------------------------------

  private parseRow(row: any): LabelLayout {
    let layoutConfig: LabelLayoutConfig;
    try {
      layoutConfig = row.layout_config ? JSON.parse(row.layout_config) : this.defaultConfig();
    } catch {
      this.logger.warn('Failed to parse layout_config for layout:', row.id);
      layoutConfig = this.defaultConfig();
    }

    return {
      id: row.id,
      name: row.name || '',
      description: row.description || '',
      orientation: row.orientation || 'landscape',
      layoutConfig,
      is_system: row.is_system ?? 1,
      created_by: row.created_by || 'system',
      created_at: row.created_at || 0,
    };
  }

  private defaultConfig(): LabelLayoutConfig {
    return {
      padding: { top: 1, right: 1.5, bottom: 1, left: 1.5 },
      qr: { x: 0, y: 0, width: 0.35, height: 1.0, errorCorrection: 'M' },
      textRegions: [],
    };
  }

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  /** Get all label layouts, ordered by name. */
  async getAll(): Promise<LabelLayout[]> {
    await this.db.init();
    this.logger.debug('Getting all label layouts...');
    const results = await this.db.exec('SELECT * FROM label_layouts ORDER BY name');
    return results.map((row) => this.parseRow(row));
  }

  /** Get a single label layout by ID. Returns null if not found. */
  async getById(id: string): Promise<LabelLayout | null> {
    await this.db.init();
    const results = await this.db.exec('SELECT * FROM label_layouts WHERE id = ?', [id]);
    return results.length > 0 ? this.parseRow(results[0]) : null;
  }

  // ---------------------------------------------------------------------------
  // Reactive Queries (RQS)
  // ---------------------------------------------------------------------------

  /** Reactive query for all label layouts. */
  createQuery(): QuerySignal<LabelLayout[]> {
    return this.db.createQuery('label_layouts', () => this.getAll());
  }
}
